import { useParams } from "react-router-dom"
import { authClient } from "~/lib/auth-client"
import { resolveOrgPermissions } from "~/lib/permissionMasks"
import { api, type RouterOutputs } from "~/trpc/react"
import { useOrganization } from "./useOrganization"

type TeamHookLoading = {
	isLoading: true
	team: undefined
	members: []
	canAccess: undefined
	orgSlug: undefined
	invalidate: () => void
}

type TeamHookLoaded = {
	isLoading: false
	team: NonNullable<RouterOutputs["team"]["getBySlug"]>
	members: NonNullable<RouterOutputs["team"]["getBySlug"]>["teamMembers"]
	canAccess: boolean
	orgSlug: string
	invalidate: () => void
}

type TeamHookNotTeamRoute = {
	isLoading: false
	team: undefined
	members: []
	canAccess: false
	orgSlug: string
	invalidate: () => void
}

export function useTeam():
	| TeamHookLoading
	| TeamHookLoaded
	| TeamHookNotTeamRoute {
	const { orgSlug = "", teamSlug = "" } = useParams<{
		orgSlug: string
		teamSlug: string
	}>()
	const { organization, isLoading: isOrgLoading } = useOrganization()
	const { data: session } = authClient.useSession()

	const utils = api.useUtils()
	const { data: team, isLoading: isTeamLoading } = api.team.getBySlug.useQuery(
		{
			organizationSlug: orgSlug,
			teamSlug
		},
		{ enabled: !!orgSlug && !!teamSlug && !!session }
	)

	const isLoading = isOrgLoading || isTeamLoading || !session

	const members = team?.teamMembers ?? []
	const orgMember = organization?.members.find(
		(member) => member.userId === session?.user.id
	)
	const isOrgAdmin = orgMember
		? resolveOrgPermissions(orgMember.roles, organization?.defaultPermissions)
				.isOrgAdmin
		: false
	const isMember = members.some(
		(member) => member.orgMember.userId === session?.user.id
	)
	const canAccess = isMember || isOrgAdmin

	if (!teamSlug || (!organization && !isOrgLoading)) {
		return {
			isLoading: false,
			team: undefined,
			members: [],
			canAccess: false,
			orgSlug: "",
			invalidate: () => {
				utils.team.getBySlug.invalidate()
			}
		}
	}

	if (isLoading) {
		return {
			isLoading: true,
			team: undefined,
			members: [],
			canAccess: undefined,
			orgSlug: undefined,
			invalidate: () => {
				utils.team.getBySlug.invalidate()
			}
		}
	}

	if (!team) {
		return {
			isLoading: false,
			team: undefined,
			members: [],
			canAccess: false,
			orgSlug: organization.slug,
			invalidate: () => {
				utils.team.getBySlug.invalidate()
			}
		}
	}

	return {
		isLoading: false,
		team,
		members: team.teamMembers,
		canAccess,
		orgSlug: organization.slug,
		invalidate: () => {
			utils.team.getBySlug.invalidate()
		}
	}
}
